/**
 * Focus for a second-factor code field — shared by `<MfaCodeInput>` and `<MfaBackupEntry>`.
 *
 * Two rules, and they are independent:
 *
 *   1. Mount focus is OPT-IN (`focusOnMount`). A field that takes focus by itself is right on a
 *      screen that is nothing but the challenge, and wrong in a card on a settings page, where it
 *      would pull focus on every page load.
 *   2. MFA-LOOP-001 is unconditional: when the host clears the code after a refused attempt
 *      (non-empty → empty) the field takes focus, so the person retypes at once. An edit that
 *      leaves the code non-empty never moves focus.
 *
 * @module @puffin-core/mfa-ui/hooks/useCodeEntryFocus
 */

import { useEffect, useRef, useState } from 'react';

/**
 * @param {Object} options
 * @param {{ current: HTMLInputElement|null }} options.inputRef - the field's input
 * @param {string} options.code - the current code
 * @param {boolean} options.focusOnMount - take focus when the field mounts
 * @returns {void}
 */
export function useCodeEntryFocus({ inputRef, code, focusOnMount }) {
  // The value at MOUNT decides; a later change of the prop is not a second mount.
  const [focusAtMount] = useState(focusOnMount);
  const prevCodeRef = useRef(code);

  useEffect(() => {
    if (focusAtMount) {
      inputRef.current.focus();
    }
  }, [focusAtMount, inputRef]);

  // MFA-LOOP-001: only the clear transition refocuses.
  useEffect(() => {
    if (prevCodeRef.current && !code) {
      inputRef.current.focus();
    }
    prevCodeRef.current = code;
  }, [code, inputRef]);
}
